import { Client } from "@/Common/Api/Client";
import { FileType } from "@/Common/Models/FileType";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { DraggableImageOverlayElementResponse } from "@/ExportableFile/Api/Dto/DraggableImageOverlayElementResponse";
import { ExportableFile, IExportableDto, IExportableFile, IExportableFileLoaded } from "@/ExportableFile/Models/ExportableFile";
import { DraggableImageOverlayElement } from "@/ExportableFile/Models/OverlayElements/DraggableImageOverlayElement";
import { OverlayElementType } from "@/ExportableFile/Models/OverlayElements/OverlayElement";
import { GenerateFileOptions } from "../IGenerateFileOptions";
import { FileTypeTemporaryFile } from "./FileTypeTemporaryFile";

interface IFileTypeRadPdf extends IExportableFile {
    patientFormId: string;
    versioningEncounterId?: string;
    formId: string | null;
}

class FileTypeRadPdf extends ExportableFile implements IFileTypeRadPdf {
    public canGenerateFileXcom: boolean = true;

    public patientFormId: string;
    public versioningEncounterId?: string; // Is the version from a specific encounter Id. Null will let the backend use latest Encounter;
    public formId: string | null;

    constructor(fields: IFileTypeRadPdf) {
        super(FileType.RADPDF);
        Object.assign(this, fields);
    }

    public getCleanCopy(): FileTypeRadPdf {
        let copyConstructor: IFileTypeRadPdf = {
            patientFormId: this.patientFormId,
            versioningEncounterId: this.versioningEncounterId,
            date: this.date,
            description: this.description,
            formId: this.formId
        };

        return new FileTypeRadPdf(copyConstructor);
    }

    public getAttachedObject_ObjectId(): string { return this.patientFormId; }

    public async getMeta(): Promise<number> {
        await this.generateFileIfNeeded({ includeOverlays: true, isExternal: false });
        return this.totalPages;
    }

    protected async generateFileIfNeededXcom(includeOverlays: boolean): Promise<IExportableFileLoaded> {
        return await this.generateFileIfNeeded({ includeOverlays: includeOverlays, isExternal: true });
    }

    protected async generateFileIfNeeded(generateFileOption: GenerateFileOptions): Promise<IExportableFileLoaded> {
        const { includeOverlays, isExternal, signPdfFormFromUserPreferences, includeOverlaysFromPreferences } = generateFileOption;
        const requestedOverlays = (includeOverlays && this.overlayElements) || [];

        // Generate a new ones with the overlays...
        const response =
            isExternal
                ? await Client.postAsyncXcom<{ TotalPages: number, ObjectId: string, SizeBytes: number, DraggableImageOverlayElements: DraggableImageOverlayElementResponse[] }>(
                    ServerOperations.Server.ExportableFile_GetRadpdfFetchUrl,
                    {
                        OverlayElements: this.serializeOverlayElements(includeOverlays),
                        patientFormId: this.patientFormId,
                        versioningEncounterId: this.versioningEncounterId,
                        SignPdfFormFromUserPreferences: !!signPdfFormFromUserPreferences,
                        IncludeOverlaysFromPreferences: !!includeOverlaysFromPreferences
                    },
                    this.clientTag,
                    this.claimId
                )
                : await Client.postAsync<{ TotalPages: number, ObjectId: string, SizeBytes: number, DraggableImageOverlayElements: DraggableImageOverlayElementResponse[] }>(
                    ServerOperations.Server.ExportableFile_GetRadpdfFetchUrl,
                    {
                        OverlayElements: this.serializeOverlayElements(includeOverlays),
                        patientFormId: this.patientFormId,
                        versioningEncounterId: this.versioningEncounterId,
                        SignPdfFormFromUserPreferences: !!signPdfFormFromUserPreferences,
                        IncludeOverlaysFromPreferences: !!includeOverlaysFromPreferences
                    }
                );

        if (!response.isSuccess)
            throw new Error(response.errors[0].Message);

        this.totalPages = response.data.TotalPages;

        let tempFile = new FileTypeTemporaryFile({
            ObjectId: response.data.ObjectId,
            totalPages: response.data.TotalPages,
            sizeBytes: response.data.SizeBytes,
            date: this.date,
            description: this.description
        });

        tempFile.isExternal = isExternal;
        if (tempFile.isExternal) {
            tempFile.clientTag = this.clientTag;
            tempFile.claimId = this.claimId;
        }

        if (response.data.DraggableImageOverlayElements?.length) {
            const signatures = DraggableImageOverlayElement.fromResponseModelArray(response.data.DraggableImageOverlayElements);

            this.applyDistinctOverlayElements(signatures, OverlayElementType.DraggableImage);
        }
        else {
            this.isDefaultPositionSignatureRequired = true;
        }

        super.setCache(tempFile, requestedOverlays);

        return {
            OriginalExportableFile: this,
            PregeneratedTemporaryFile: tempFile,
            FetchUrl:
                isExternal
                    ? tempFile.FetchUrlXcom(this.clientTag, this.claimId)
                    : tempFile.FetchUrl
        } as IExportableFileLoaded;
    }

    public async persistSignature(encounterId: string, lastModificationDateUtc: Date | null = null): Promise<void> {
        const signedFile = await this.toFileDto();
        return this.saveSignAuthorization(signedFile, encounterId, lastModificationDateUtc);
    }

    private async saveSignAuthorization(signedFile: IExportableDto, encounterId: string, lastModificationDateUtc: Date | null = null): Promise<void> {
        const response = await Client.postAsync(
            ServerOperations.Server.Myle_SaveSignedPrintAuthorization,
            {
                ExportableFileDtoSigned: signedFile,
                OneTimeOnly: true,
                EncounterId: encounterId,
                LastModificationDateUtc: lastModificationDateUtc
            }
        );

        if (!response.isSuccess)
            sessionStore.showPopup(response.errors);
    }

    public async saveOverlayPositionsIfNeeded(): Promise<void> {
        await Client.postAsync(
            ServerOperations.Server.ExportableFile_RadPdf_SaveSignaturePositions,
            {
                OverlayElements: this.serializeOverlayElements(true),
                patientFormId: this.patientFormId
            }
        );
    }
}

export { FileTypeRadPdf };
